"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { createClient } from "../lib/supabase/client";
import { toast } from "sonner";
import type { RealtimeChannel } from "@supabase/supabase-js";

interface GenerationStatus {
  id: string;
  sessionId?: string;
  status: "PENDING" | "PROCESSING" | "DONE" | "ERROR";
  images: string[];
  resultUrl: string | null;
  errorMessage?: string;
  progress?: number;
}

export function useCharacterGenerationRealtime(id: string | null, sessionId?: string | null) {
  const [status, setStatus] = useState<GenerationStatus | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const supabaseRef = useRef(createClient());
  const toastShownRef = useRef<Set<string>>(new Set());

  const fetchInitialStatus = useCallback(async () => {
    if (!id) return null;

    try {
      // Build query with session filtering if provided
      let query = supabaseRef.current
        .from("character_generations")
        .select("*")
        .eq("id", id);
      
      // Add session filter for multi-user isolation
      if (sessionId) {
        query = query.eq("session_id", sessionId);
      }
      
      const { data, error } = await query.single() as any;

      if (error || !data) {
        if (error) console.error("[CharacterGeneration] Error fetching initial status:", error);
        return null;
      }

      return {
        id: data.id,
        sessionId: data.session_id,
        status: data.status,
        images: Array.isArray(data.images) ? data.images : JSON.parse(data.images as string || "[]"),
        resultUrl: data.result_url,
        errorMessage: data.error_message,
      } as GenerationStatus;
    } catch (error) {
      console.error("[CharacterGeneration] Error fetching initial status:", error);
      return null;
    }
  }, [id, sessionId]);

  const showToastOnce = useCallback((key: string, message: string, type: "success" | "error" | "info") => {
    if (!toastShownRef.current.has(key)) {
      toastShownRef.current.add(key);
      if (type === "success") {
        toast.success(message);
      } else if (type === "error") {
        toast.error(message);
      } else {
        toast.info(message);
      }
    }
  }, []);

  useEffect(() => {
    if (!id) {
      setStatus(null);
      setIsConnected(false);
      toastShownRef.current.clear();
      return;
    }

    let mounted = true;
    const supabaseClient = supabaseRef.current;

    const setupRealtime = async () => {
      // Fetch initial status
      const initialStatus = await fetchInitialStatus();
      if (mounted && initialStatus) {
        setStatus(initialStatus);

        // If already completed or failed, don't subscribe
        if (initialStatus.status === "DONE" || initialStatus.status === "ERROR") {
          if (initialStatus.status === "DONE") {
            showToastOnce(`done-${id}`, "3D model generation complete!", "success");
          } else {
            showToastOnce(`error-${id}`, initialStatus.errorMessage || "Generation failed", "error");
          }
          return;
        }
      }

      // Subscribe to realtime updates with unique channel name
      const channelName = sessionId ? `character-gen-${id}-${sessionId}` : `character-gen-${id}`;
      console.log(`[CharacterGeneration] Setting up realtime subscription on channel: ${channelName}`);
      
      const channel = supabaseClient
        .channel(channelName)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "character_generations",
            filter: `id=eq.${id}`,
          },
          (payload) => {
            console.log(`[CharacterGeneration] Realtime update received:`, payload);

            const newData = payload.new as any;
            
            // Verify session match for multi-user isolation
            if (sessionId && newData.session_id !== sessionId) {
              console.warn("[CharacterGeneration] Received update for different session, ignoring");
              return;
            }
            
            const updatedStatus: GenerationStatus = {
              id: newData.id,
              sessionId: newData.session_id,
              status: newData.status,
              images: Array.isArray(newData.images) ? newData.images : JSON.parse(newData.images || "[]"),
              resultUrl: newData.result_url,
              errorMessage: newData.error_message,
            };

            setStatus(updatedStatus);

            // Show toast notifications for status changes
            if (newData.status === "PROCESSING") {
              showToastOnce(`processing-${id}`, "AI is generating your 3D model...", "info");
            } else if (newData.status === "DONE") {
              showToastOnce(`done-${id}`, "Your 3D model has been generated!", "success");
            } else if (newData.status === "ERROR") {
              showToastOnce(
                `error-${id}`,
                newData.error_message || "Failed to generate model. Please try again.",
                "error"
              );
            }
          },
        )
        .subscribe((status) => {
          console.log("[CharacterGeneration] Subscription status:", status);
          setIsConnected(status === "SUBSCRIBED");
        });

      channelRef.current = channel;
    };

    setupRealtime();

    // Cleanup function
    return () => {
      mounted = false;
      if (channelRef.current) {
        console.log(`[CharacterGeneration] Unsubscribing from channel`);
        supabaseClient.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setIsConnected(false);
    };
  }, [id, sessionId, fetchInitialStatus, showToastOnce]);

  const refresh = useCallback(async () => {
    const freshStatus = await fetchInitialStatus();
    if (freshStatus) {
      setStatus(freshStatus);
    }
  }, [fetchInitialStatus]);

  return {
    status,
    isConnected,
    refresh,
  };
}
